/* BoerenShot theme scripts. Author: Frontpy. Loaded deferred. */
(() => {
  'use strict';

  const settings = window.BoerenShotTheme || {};

  /* 1. Section load animations: sections with .animate-section get .animate--shown when they scroll into view. */
  if (settings.loadAnimations) {
    const bottomMargin = 50;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate--shown');
          else if (settings.repeatAnimations) entry.target.classList.remove('animate--shown');
        });
      },
      { rootMargin: `0px 0px -${bottomMargin}px 0px` }
    );
    const init = (root = document, editorEvent = false) => {
      root.querySelectorAll('.animate-section').forEach((el) => {
        if (editorEvent) el.classList.add('animate--shown');
        else observer.observe(el);
      });
    };
    init();
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => init(e.target, true));
      document.addEventListener('shopify:section:reorder', () => init(document, true));
    }
  }

  /* 2. External links open in a new tab. */
  Array.from(document.links).forEach((link) => {
    if (link.hostname && link.hostname !== window.location.hostname) {
      link.target = '_blank';
      link.rel = 'noreferrer noopener';
    }
  });

  /* 3. Optional: block right click, text selection, image dragging and devtools shortcuts. */
  if (settings.disableInspect) {
    window.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('selectstart', (e) => e.preventDefault());
    document.querySelectorAll('img').forEach((img) => img.addEventListener('dragstart', (e) => e.preventDefault()));
    const ctrlShift = (e, key) => e.ctrlKey && e.shiftKey && e.key.toUpperCase() === key;
    document.addEventListener('keydown', (e) => {
      if (e.key === 'F12' || ctrlShift(e, 'I') || ctrlShift(e, 'J') || ctrlShift(e, 'C') || (e.ctrlKey && e.key.toUpperCase() === 'U')) {
        e.preventDefault();
      }
    });
  }

  /* 4. Scroll to top button. */
  class ScrollToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.after = Number(this.dataset.displayAfter || 0);
      this.button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      if (this.after > 0) {
        this.onScroll = this.toggle.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.toggle();
      }
    }
    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
    toggle() {
      this.button.style.display = window.scrollY > this.after ? '' : 'none';
    }
  }
  if (!customElements.get('scroll-to-top')) customElements.define('scroll-to-top', ScrollToTop);


  /* 5. Announcement bar: rotates its messages when more messages than columns are shown. */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.bs-announcement__item'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.items[0].classList.add('is-active');
      const speed = Math.max(2, Number(this.dataset.speed || 5)) * 1000;
      this.timer = setInterval(() => {
        const rotating = window.matchMedia('(min-width: 750px)').matches ? this.dataset.rotateDesktop === 'true' : this.dataset.rotateMobile === 'true';
        if (!rotating) return;
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, speed);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* Copy button (discount codes): copies data-code and shows the success state for two seconds. */
  class CopyButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', () => {
        const code = (this.dataset.code || this.dataset.content || '').trim();
        if (!code) return;
        navigator.clipboard.writeText(code).then(() => {
          this.classList.add('success');
          this.dataset.success = 'true';
          setTimeout(() => { this.classList.remove('success'); this.dataset.success = 'false'; }, 2000);
        });
      });
    }
  }
  if (!customElements.get('copy-button')) customElements.define('copy-button', CopyButton);


  /* Logo cloud: clicking a logo (or the autoplay timer) shows the text that belongs to it. */
  class LogoCloud extends HTMLElement {
    connectedCallback() {
      this.logos = Array.from(this.querySelectorAll('.bs-logos__logo'));
      this.contents = Array.from(this.querySelectorAll('.bs-logos__content'));
      if (this.classList.contains('bs-logos--image-only') || !this.contents.length) return;
      this.index = 0;
      this.logos.forEach((logo) => logo.addEventListener('click', () => {
        this.index = Number(logo.dataset.index || 0);
        this.show();
        clearInterval(this.timer);
      }));
      this.show();
      const interval = Number(this.dataset.autoplayInterval || 0);
      if (interval > 0) this.timer = setInterval(() => { this.index = (this.index + 1) % this.logos.length; this.show(); }, interval);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    show() {
      this.logos.forEach((l, i) => l.classList.toggle('is-active', i === this.index));
      this.contents.forEach((c, i) => c.classList.toggle('is-active', i === this.index));
    }
  }
  if (!customElements.get('logo-cloud')) customElements.define('logo-cloud', LogoCloud);


  /* Coverflow slider (Swiper 11, loaded deferred from the theme assets). */
  class CoverSlider extends HTMLElement {
    connectedCallback() {
      const start = () => {
        if (typeof window.Swiper !== 'function') { setTimeout(start, 50); return; }
        if (this.swiper) this.swiper.destroy(true, true);
        this.classList.remove('is-preview');
        const view = Number(this.dataset.view || 4);
        this.swiper = new window.Swiper(this, {
          effect: 'coverflow',
          centeredSlides: true,
          loop: true,
          loopAdditionalSlides: 4,
          slidesPerView: 'auto',
          initialSlide: 1,
          navigation: { nextEl: this.querySelector('.bs-cover__arrow--next'), prevEl: this.querySelector('.bs-cover__arrow--prev') },
          coverflowEffect: { rotate: Number(this.dataset.rotate || 0), stretch: 0, depth: 200, modifier: 1, slideShadows: false },
          breakpoints: {
            320: { slidesPerView: Number(this.dataset.viewMobile || 1.5) },
            768: { slidesPerView: Number(this.dataset.viewTablet || Math.round(view / 2)) },
            1024: { slidesPerView: view },
          },
        });
      };
      start();
    }
    disconnectedCallback() {
      if (this.swiper) this.swiper.destroy(true, true);
    }
  }
  if (!customElements.get('cover-slider')) customElements.define('cover-slider', CoverSlider);

  /* 6. Parallax hero: every layer moves, zooms and rotates with the scroll position.
        scroll_y: vertical movement as a percentage of the distance the section has scrolled;
        scroll_x: horizontal movement in % at full progress; zoom: scale at full progress (100 = none);
        rotation: 1 = 3.6 degrees at full progress. */
  class ParallaxHero extends HTMLElement {
    connectedCallback() {
      this.layers = Array.from(this.querySelectorAll('.parallax-hero__layer'));
      if (!this.layers.length) return;
      this.startOnEnter = this.dataset.animationStart === 'bottom';
      this.onScroll = () => requestAnimationFrame(() => this.update());
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    update() {
      const { top, height } = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      if (top > viewport || top + height < 0) return;
      let progress;
      if (this.startOnEnter) {
        progress = Math.min((viewport - top) / (viewport + height), 1);
      } else {
        const scrolled = top >= 0 ? 0 : -top;
        if (scrolled > height) return;
        progress = Math.min(scrolled / height, 1);
      }
      this.layers.forEach((layer) => {
        const y = top * (parseInt(layer.dataset.scrollY || 0, 10) / 100);
        const x = parseInt(layer.dataset.scrollX || 0, 10) * progress;
        const zoom = parseInt(layer.dataset.zoom || 100, 10);
        const scale = (100 + (zoom - 100) * progress) / 100;
        const rotate = 3.6 * parseInt(layer.dataset.rotation || 0, 10) * progress;
        layer.style.transform = `translateY(${y}px) translateX(${x}%) scale(${scale}) rotate(${rotate}deg)`;
      });
    }
  }
  if (!customElements.get('parallax-hero')) customElements.define('parallax-hero', ParallaxHero);

  /* 7. Sticky add-to-cart bar on the product page.
        Shows when the real buy button has scrolled out of view (or always), mirrors variant and price,
        and submits the real product form so the cart drawer logic stays in one place. */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.trigger = document.getElementById(this.dataset.trigger);
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.button = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.buttonPrice = this.querySelector('[data-sticky-button-price]');
      this.select = this.querySelector('[data-sticky-variant-select]');
      this.afterScroll = this.dataset.afterScroll === 'true';
      if (this.button) this.button.addEventListener('click', () => this.submit());
      if (this.select) this.select.addEventListener('change', () => this.syncToPage());
      if (this.afterScroll && this.trigger && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const below = entry.boundingClientRect.top > window.innerHeight;
          this.classList.toggle('sticky-atc--visible', !entry.isIntersecting && !below);
        });
        this.observer.observe(this.trigger);
      } else {
        this.classList.add('sticky-atc--visible');
      }
      this.onVariant = (e) => this.syncFromPage(e);
      document.addEventListener('variant:changed', this.onVariant);
      /* Dawn variant pickers replace the price markup through the Section Rendering API; watch it. */
      const price = document.getElementById(`price-${this.sectionId}`);
      if (price && 'MutationObserver' in window) {
        this.priceObserver = new MutationObserver(() => this.copyPrice());
        this.priceObserver.observe(price, { childList: true, subtree: true });
      }
      const pageButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (pageButton && 'MutationObserver' in window) {
        this.buttonObserver = new MutationObserver(() => this.copyAvailability());
        this.buttonObserver.observe(pageButton, { attributes: true, attributeFilter: ['disabled'] });
      }
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.buttonObserver) this.buttonObserver.disconnect();
      document.removeEventListener('variant:changed', this.onVariant);
    }
    currentVariantId() {
      const input = this.form ? this.form.querySelector('input[name="id"]') : null;
      return input ? input.value : null;
    }
    copyPrice() {
      const price = document.getElementById(`price-${this.sectionId}`);
      const target = this.querySelector('[data-sticky-price]');
      if (price && target) target.innerHTML = price.innerHTML;
      if (this.buttonPrice && price) {
        const sale = price.querySelector('.price-item--sale');
        const regular = price.querySelector('.price__regular .price-item--regular');
        const txt = (sale && sale.offsetParent !== null ? sale : regular || sale);
        if (txt) this.buttonPrice.textContent = `• ${txt.textContent.trim()}`;
      }
      if (this.select) {
        const id = this.currentVariantId();
        if (id && this.select.value !== id) this.select.value = id;
      }
    }
    copyAvailability() {
      const pageButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!pageButton || !this.button) return;
      const disabled = pageButton.hasAttribute('disabled');
      this.button.toggleAttribute('disabled', disabled);
      if (this.label) this.label.textContent = disabled ? this.label.dataset.soldOut : this.label.dataset.label;
    }
    syncFromPage() { this.copyPrice(); this.copyAvailability(); }
    syncToPage() {
      /* Select the matching option values in the page picker; Dawn then updates URL, price and form input. */
      const picker = document.querySelector(`#variant-radios-${this.sectionId}, #variant-selects-${this.sectionId}`);
      if (!picker) return;
      if (typeof picker.selectVariant === 'function') {
        picker.selectVariant(this.select.value);
        return;
      }
      const data = picker.querySelector('script[type="application/json"]');
      if (!data) return;
      const variant = JSON.parse(data.textContent).find((v) => String(v.id) === this.select.value);
      if (!variant) return;
      variant.options.forEach((value, i) => {
        const radio = picker.querySelector(`fieldset:nth-of-type(${i + 1}) input[value="${CSS.escape(value)}"]`);
        if (radio) { radio.checked = true; return; }
        const select = picker.querySelectorAll('select')[i];
        if (select) select.value = value;
      });
      picker.dispatchEvent(new Event('change', { bubbles: true }));
    }
    submit() {
      if (this.button.hasAttribute('disabled')) return;
      const pageButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (pageButton) { pageButton.click(); return; }
      if (this.form) this.form.requestSubmit();
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* 8. Simple slider: scroll-snap track with dots and arrows. Active only at the breakpoints given by
        .bs-slider--mobile (max 749px) and .bs-slider--desktop (min 750px); otherwise it is a plain grid. */
  class BsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bs-slider__track');
      this.slides = Array.from(this.querySelectorAll('.bs-slider__slide'));
      this.dots = this.querySelector('.bs-slider__dots');
      this.prev = this.querySelector('.bs-slider__arrow--prev');
      this.next = this.querySelector('.bs-slider__arrow--next');
      if (!this.track || this.slides.length < 2) return;
      this.loop = this.dataset.loop === 'true';
      this.buildDots();
      this.prev && this.prev.addEventListener('click', () => this.go(this.index() - 1));
      this.next && this.next.addEventListener('click', () => this.go(this.index() + 1));
      this.track.addEventListener('scroll', () => { clearTimeout(this.t); this.t = setTimeout(() => this.update(), 60); }, { passive: true });
      this.update();
      if (this.dataset.autoplay === 'true') {
        const ms = Math.max(2, Number(this.dataset.autoplaySpeed || 5)) * 1000;
        this.timer = setInterval(() => { if (this.isActive()) this.go(this.index() + 1); }, ms);
        this.addEventListener('pointerdown', () => clearInterval(this.timer));
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    isActive() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      return mobile ? this.classList.contains('bs-slider--mobile') : this.classList.contains('bs-slider--desktop');
    }
    buildDots() {
      if (!this.dots) return;
      this.dots.innerHTML = '';
      this.slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bs-slider__dot';
        b.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
        b.addEventListener('click', () => this.go(i));
        this.dots.appendChild(b);
      });
    }
    index() {
      const left = this.track.scrollLeft;
      let best = 0, bestDist = Infinity;
      this.slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - left); if (d < bestDist) { bestDist = d; best = i; } });
      return best;
    }
    go(i) {
      const n = this.slides.length;
      if (this.loop) i = (i + n) % n; else i = Math.max(0, Math.min(n - 1, i));
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: 'smooth' });
    }
    update() {
      const i = this.index();
      if (this.dots) Array.from(this.dots.children).forEach((d, j) => d.classList.toggle('is-active', j === i));
      if (!this.loop) {
        const end = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
        this.prev && this.prev.toggleAttribute('disabled', i === 0);
        this.next && this.next.toggleAttribute('disabled', end);
      }
    }
  }
  if (!customElements.get('bs-slider')) customElements.define('bs-slider', BsSlider);

  /* 9. Contact form: required fields show their own message instead of the browser bubble. */
  document.querySelectorAll('form#ContactForm').forEach((form) => {
    form.addEventListener('submit', (e) => {
      let valid = true;
      form.querySelectorAll('.field-wrapper[data-required="true"]').forEach((wrap) => {
        const input = wrap.querySelector('input, textarea');
        const msg = wrap.querySelector('.field-wrapper__error-msg');
        const empty = !input || !input.value.trim();
        const badEmail = input && input.type === 'email' && input.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value);
        const bad = empty || badEmail;
        if (msg) msg.classList.toggle('hidden', !bad);
        wrap.classList.toggle('field-wrapper--error', bad);
        if (bad) { valid = false; if (input && valid === false && !form.querySelector('.field-wrapper--error input:focus')) input.focus(); }
      });
      if (!valid) e.preventDefault();
    });
    form.querySelectorAll('.field-wrapper[data-required="true"] input, .field-wrapper[data-required="true"] textarea').forEach((input) => {
      input.addEventListener('input', () => {
        const wrap = input.closest('.field-wrapper');
        if (input.value.trim()) { wrap.classList.remove('field-wrapper--error'); const m = wrap.querySelector('.field-wrapper__error-msg'); m && m.classList.add('hidden'); }
      });
    });
  });

  /* 10. Discount code field on the cart page: applies the code through Shopify's discount URL. */
  class DiscountField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.button = this.querySelector('button');
      this.error = this.querySelector('.bs-discount-field__error');
      this.button.addEventListener('click', () => this.apply());
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.apply(); } });
    }
    apply() {
      const code = this.input.value.trim();
      if (!code) { this.error.textContent = this.dataset.error; this.error.classList.remove('hidden'); return; }
      this.error.classList.add('hidden');
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
  }
  if (!customElements.get('discount-field')) customElements.define('discount-field', DiscountField);

  /* 11. Track order: loads the 17TRACK widget only after the visitor asks for a result. */
  class TrackOrder extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.button = this.querySelector('[data-track-button]');
      this.button.addEventListener('click', () => this.track());
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.track(); } });
    }
    track() {
      const num = this.input.value.trim();
      if (!num) { alert(this.button.dataset.emptyMessage); return; }
      const run = () => window.YQV5.trackSingle({ YQ_ContainerId: 'YQContainer', YQ_Height: 560, YQ_Fc: '0', YQ_Lang: document.documentElement.lang || 'nl', YQ_Num: num });
      if (window.YQV5) { run(); return; }
      const script = document.createElement('script');
      script.src = 'https://www.17track.net/externalcall.js';
      script.onload = run;
      document.head.appendChild(script);
    }
  }
  if (!customElements.get('track-order')) customElements.define('track-order', TrackOrder);

  /* 12. Upsell variant dropdowns live outside their <form>; keep the hidden id input in sync. */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-upsell-variant]');
    if (!select) return;
    const form = document.getElementById(select.dataset.upsellVariant);
    const input = form && form.querySelector('input[name="id"]');
    if (input) input.value = select.value;
    const button = form && form.querySelector('[name="add"]');
    if (button) button.toggleAttribute('disabled', select.selectedOptions[0].disabled);
  });
})();
